<template>
  <section id="how-it-works" class="py-10 md:py-16 scroll-mt-20">
    <!-- How DuckNSeek Works - Process Steps -->
    <div class="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-gray-100">
      <div class="text-center mb-8">
        <h3 class="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
          How <span class="text-yellow-500">DuckNSeek</span> Works
        </h3>
        <p class="text-lg text-gray-600 mb-6">
          Turn your cruise into an interactive adventure in 3 simple steps
        </p>

        <!-- See It In Action CTA -->
        <div class="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-5 border border-blue-100 max-w-2xl mx-auto">
          <div class="flex items-center justify-center gap-2 mb-4">
            <v-icon color="blue" size="20" :class="{'animate-float-subtle': !reducedMotion}">mdi-play-circle</v-icon>
            <h4 class="text-lg md:text-xl font-bold text-gray-800">See It In Action</h4>
            <v-icon color="purple" size="20" :class="{'animate-float-subtle': !reducedMotion}">mdi-sparkles</v-icon>
          </div>
          
          <div class="flex flex-col sm:flex-row gap-3 justify-center">
            <!-- Admin Demo -->
            <button 
              @click="navigateToDemo"
              :disabled="adminLoading"
              class="group flex items-center justify-center bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 disabled:from-blue-400 disabled:to-blue-500 text-white px-5 py-2.5 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 min-w-[160px] disabled:cursor-not-allowed disabled:transform-none"
            >
              <v-progress-circular v-if="adminLoading" indeterminate size="16" width="2" class="mr-2"></v-progress-circular>
              <v-icon v-else size="16" class="mr-2 group-hover:scale-110 transition-transform">mdi-view-dashboard</v-icon>
              <div class="text-left">
                <div class="text-sm font-semibold">{{ adminLoading ? 'Loading...' : 'Hider Demo' }}</div>
                <div class="text-xs opacity-90">{{ adminLoading ? 'Please wait' : 'Manage ducks' }}</div>
              </div>
            </button>

            <!-- Duck Demo -->
            <button 
              @click="navigateToDuckDemo"
              :disabled="duckLoading"
              class="group flex items-center justify-center bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 disabled:from-yellow-400 disabled:to-orange-400 text-white px-5 py-2.5 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 min-w-[160px] disabled:cursor-not-allowed disabled:transform-none"
            >
              <v-progress-circular v-if="duckLoading" indeterminate size="16" width="2" class="mr-2"></v-progress-circular>
              <v-icon v-else size="16" class="mr-2 group-hover:scale-110 transition-transform">mdi-duck</v-icon>
              <div class="text-left">
                <div class="text-sm font-semibold">{{ duckLoading ? 'Loading...' : 'Finder Demo' }}</div>
                <div class="text-xs opacity-90">{{ duckLoading ? 'Please wait' : 'Discover duck' }}</div>
              </div>
            </button>
          </div>
          
          <div class="flex items-center justify-center text-xs text-gray-500 mt-3">
            <v-icon size="12" class="mr-1 opacity-60">mdi-timer-outline</v-icon>
            <span>2 minutes each • No signup needed</span>
          </div>
        </div>
      </div>

      <!-- Process Steps -->
      <div class="relative">
        <!-- Connection Line -->
        <div class="hidden lg:block absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[80%] h-1 bg-gradient-to-r from-yellow-500/20 via-blue-400/40 to-yellow-500/20 rounded-full z-0"></div>
        
        <div class="grid md:grid-cols-3 gap-4 md:gap-6 relative z-10">
          <!-- Step 1 -->
          <div class="text-center transform hover:-translate-y-2 transition-all duration-300">
            <div class="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-4 md:p-5 shadow-lg border border-yellow-100 h-full flex flex-col">
              <div class="flex items-center justify-center gap-2.5 mb-2">
                <div class="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center text-white font-bold text-base shadow-md">1</div>
                <v-icon color="yellow" size="28" :class="{'animate-float': !reducedMotion}">mdi-shopping</v-icon>
              </div>
              <div class="flex-1 flex flex-col">
                <h4 class="text-lg md:text-xl font-semibold text-gray-800 mb-2 leading-snug">Order Your Ducks</h4>
                <p class="text-gray-600 text-sm md:text-base leading-relaxed flex-1">
                  Choose your package and cruise details. Each duck gets a <span class="font-bold text-yellow-600">unique QR code</span> and <span class="font-bold text-yellow-600">custom design</span>.
                </p>
              </div>
            </div>
          </div>

          <!-- Step 2 -->
          <div class="text-center transform hover:-translate-y-2 transition-all duration-300">
            <div class="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-4 md:p-5 shadow-lg border border-blue-100 h-full flex flex-col">
              <div class="flex items-center justify-center gap-2.5 mb-2">
                <div class="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-base shadow-md">2</div>
                <v-icon color="blue" size="28" :class="{'animate-float-delay': !reducedMotion}">mdi-map-marker-plus</v-icon>
              </div>
              <div class="flex-1 flex flex-col">
                <h4 class="text-lg md:text-xl font-semibold text-gray-800 mb-2 leading-snug">Hide Around the Ship</h4>
                <p class="text-gray-600 text-sm md:text-base leading-relaxed flex-1">
                  Place your ducks in <span class="font-bold text-blue-600">creative spots</span> around the cruise ship. When someone finds one, they <span class="font-bold text-blue-600">scan the QR code</span>.
                </p>
              </div>
            </div>
          </div>

          <!-- Step 3 -->
          <div class="text-center transform hover:-translate-y-2 transition-all duration-300">
            <div class="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-4 md:p-5 shadow-lg border border-purple-100 h-full flex flex-col">
              <div class="flex items-center justify-center gap-2.5 mb-2">
                <div class="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-base shadow-md">3</div>
                <v-icon color="purple" size="28" :class="{'animate-float-reverse': !reducedMotion}">mdi-account-group</v-icon>
              </div>
              <div class="flex-1 flex flex-col">
                <h4 class="text-lg md:text-xl font-semibold text-gray-800 mb-2 leading-snug">Get Messages & Track</h4>
                <p class="text-gray-600 text-sm md:text-base leading-relaxed flex-1">
                  Receive <span class="font-bold text-purple-600">instant notifications</span> when found. Read <span class="font-bold text-purple-600">messages</span> and <span class="font-bold text-purple-600">track</span> each duck's journey.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- QR Templates Section -->
        <div class="mt-8">
          <div class="text-center mb-4">
            <div class="flex items-center justify-center gap-2 mb-1.5">
              <v-icon color="blue" size="20">mdi-palette-outline</v-icon>
              <h4 class="text-lg md:text-xl font-bold text-gray-800">
                Choose Your <span class="text-blue-500">QR Style</span>
              </h4>
            </div>
            <p class="text-xs md:text-sm text-gray-600 max-w-lg mx-auto">
              Pick your favorite design — all scan the same!
            </p>
          </div>

          <!-- QR Templates: Mobile Carousel + Desktop Grid -->
          <div class="max-w-5xl mx-auto">
            <!-- Mobile Carousel -->
            <div class="md:hidden">
              <v-carousel
                class="qr-carousel rounded-xl bg-transparent"
                :height="260"
                cycle
                :show-arrows="true"
                hide-delimiter-background
                :interval="5000"
              >
                <v-carousel-item v-for="s in qrSlides" :key="s.key">
                  <div class="h-full w-full flex flex-col items-center justify-center px-4 py-1">
                    <!-- Image Container -->
                    <div class="relative w-full max-w-md mb-1 flex-1 flex items-center justify-center">
                      <div class="w-full h-full flex items-center justify-center">
                        <img 
                          :src="s.img" 
                          :alt="s.alt" 
                          class="max-w-full max-h-full object-contain" 
                          loading="lazy" 
                        />
                      </div>
                    </div>
                    
                    <!-- Label -->
                    <div class="text-center pb-6">
                      <div class="flex items-center justify-center gap-2 mb-1">
                        <v-icon :color="s.iconColor" size="18">{{ s.icon }}</v-icon>
                        <h5 class="font-semibold text-sm text-gray-800">{{ s.title }}</h5>
                      </div>
                      <p class="text-xs text-gray-600">{{ s.desc }}</p>
                    </div>
                  </div>
                </v-carousel-item>
              </v-carousel>
            </div>

            <!-- Desktop Grid -->
            <div class="hidden md:grid grid-cols-3 gap-5">
              <div
                v-for="s in qrSlides"
                :key="s.key"
                class="bg-white rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow p-4"
              >
                <div class="flex items-center justify-center mb-3 h-40">
                  <img
                    :src="s.img"
                    :alt="s.alt"
                    class="max-h-full max-w-full object-contain"
                    loading="lazy"
                  />
                </div>
                <div class="text-center">
                  <div class="flex items-center justify-center gap-2 mb-1">
                    <v-icon :color="s.iconColor" size="18">{{ s.icon }}</v-icon>
                    <h5 class="font-semibold text-base text-gray-800">{{ s.title }}</h5>
                  </div>
                  <p class="text-sm text-gray-600">{{ s.desc }}</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Section Divider -->
          <div class="mt-6">
            <div class="mx-auto max-w-4xl">
              <div class="h-px w-full bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Rarity System - Ultra Compact -->
      <div class="bg-gradient-to-br from-yellow-50/40 to-orange-50/40 rounded-2xl p-5 md:p-6 border border-yellow-200">
        <!-- Header -->
        <div class="text-center mb-5">
          <div class="flex items-center justify-center gap-2 mb-1.5">
            <v-icon color="warning" size="24">mdi-trophy</v-icon>
            <h3 class="text-xl md:text-2xl font-bold text-slate-800">
              <span class="text-yellow-500">Rarity System</span>
            </h3>
          </div>
          <p class="text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
            Scan the QR code on a duck's tag to collect it! You'll earn points based on the duck's rarity and compete on the leaderboard.
          </p>
        </div>

        <!-- Rarity Levels -->
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 justify-center items-stretch gap-3 md:gap-4 max-w-4xl mx-auto">
          <div 
            v-for="rarity in rarities"
            :key="rarity.name"
            class="flex-1 flex flex-row sm:flex-col items-center sm:justify-between gap-3 sm:gap-2.5 bg-white rounded-xl p-3 sm:p-4 md:p-5 shadow-sm border transition-all hover:shadow-md"
            :class="rarity.borderClass"
          >
            <!-- Duck Image -->
            <div class="relative shrink-0">
              <img 
                :src="`/duckRarity/${rarity.name}.webp`" 
                :alt="`${rarity.name} duck`"
                class="w-14 h-14 sm:w-20 sm:h-20 md:w-24 md:h-24 object-contain"
              />
            </div>
            
            <!-- Info Container -->
            <div class="flex-1 flex flex-col min-w-0 text-left sm:text-center sm:items-center">
              
              <!-- Mobile: Name / Desktop: Badge -->
              <div class="sm:hidden font-bold text-gray-800 text-sm leading-tight mb-0.5">
                {{ rarity.name }}
              </div>
              
              <div 
                class="hidden sm:block px-3 py-1 rounded-full text-xs md:text-sm font-bold tracking-wide mb-1 order-1"
                :class="rarity.badgeClass"
              >
                {{ rarity.name }}
              </div>
              
              <!-- Stars (Shared but different positioning context) -->
              <div class="flex flex-wrap items-center sm:justify-center gap-0.5 order-3 sm:mt-1">
                <v-icon 
                  v-for="i in rarity.stars" 
                  :key="i" 
                  size="14" 
                  :color="rarity.starColor"
                >
                  mdi-star
                </v-icon>
                <v-icon 
                  v-for="i in (3 - rarity.stars)" 
                  :key="'empty-' + i" 
                  size="14" 
                  color="grey-lighten-2"
                >
                  mdi-star-outline
                </v-icon>
              </div>

              <!-- Desktop-only Points (to maintain order: Image -> Badge -> Points -> Stars) -->
              <div class="hidden sm:block text-center order-2">
                <div class="text-3xl md:text-4xl font-extrabold leading-none" :class="rarity.pointsClass">
                  {{ rarity.pointsFind }}
                </div>
                <div class="text-xs md:text-sm text-gray-500 mt-0.5">points</div>
              </div>
            </div>

            <!-- Mobile-only Points (Right aligned) -->
            <div class="sm:hidden text-right shrink-0">
              <div class="text-2xl font-extrabold leading-none" :class="rarity.pointsClass">
                {{ rarity.pointsFind }}
              </div>
              <div class="text-[10px] text-gray-500 font-medium">PTS</div>
            </div>
          </div>
        </div>

        <!-- Leaderboard CTA -->
        <div class="text-center mt-5">
          <button 
            @click="$emit('scroll-to-section', 'leaderboard')"
            class="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white rounded-xl font-semibold text-sm shadow-md hover:shadow-lg transition-all"
          >
            <v-icon size="18">mdi-podium-gold</v-icon>
            <span>View Leaderboard</span>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';

interface Props {
  reducedMotion?: boolean;
}

withDefaults(defineProps<Props>(), {
  reducedMotion: false
});

defineEmits<{
  (e: 'scroll-to-section', sectionId: string): void;
}>();

const router = useRouter();

const adminLoading = ref(false);
const duckLoading = ref(false);

const navigateToDemo = async () => {
  if (adminLoading.value) return;

  try {
    adminLoading.value = true;
    await router.push('/demo');
  } catch (error) {
    console.error('Navigation to demo failed:', error);
    setTimeout(() => router.push('/demo'), 100);
  } finally {
    setTimeout(() => {
      adminLoading.value = false;
    }, 500);
  }
};

const navigateToDuckDemo = async () => {
  if (duckLoading.value) return;

  try {
    duckLoading.value = true;
    await router.push('/demo/duck/101');
  } catch (error) {
    console.error('Navigation to duck demo failed:', error);
    setTimeout(() => router.push('/demo/duck/101'), 100);
  } finally {
    setTimeout(() => {
      duckLoading.value = false;
    }, 500);
  }
};

const qrSlides = [
  {
    key: 'standard',
    img: '/TemplateImages/StandardQRLayout.webp',
    alt: 'Standard QR Layout Template',
    title: 'Standard',
    desc: 'Full details + branding',
    icon: 'mdi-card-text-outline',
    iconColor: 'primary'
  },
  {
    key: 'cruise-themed',
    img: '/TemplateImages/ModernTemplate.webp',
    alt: 'Cruise Themed Template',
    title: 'Cruise Themed',
    desc: 'Colorful, nautical look',
    icon: 'mdi-palette-outline',
    iconColor: 'purple'
  },
  {
    key: 'minimal',
    img: '/TemplateImages/JustQRCode.webp',
    alt: 'Just QR Code Template',
    title: 'Minimal',
    desc: 'Clean, QR-first design',
    icon: 'mdi-qrcode-scan',
    iconColor: 'grey-darken-2'
  }
] as const;

const rarities = ref([
  {
    name: 'COMMON',
    pointsFind: 1,
    pointsHide: 1,
    stars: 1,
    starColor: 'yellow',
    description: 'Easy to find, perfect for beginners. Great for high-traffic areas!',
    iconColor: '#eab308',
    iconBgClass: 'bg-yellow-100',
    badgeClass: 'bg-yellow-500 text-white',
    borderClass: 'border-yellow-300',
    glowClass: 'bg-yellow-500',
    cardClass: '',
    pointsClass: 'text-yellow-600'
  },
  {
    name: 'RARE',
    pointsFind: 2,
    pointsHide: 2,
    stars: 2,
    starColor: 'purple',
    description: 'Harder to spot, bigger bragging rights. Hide them strategically!',
    iconColor: '#9333ea',
    iconBgClass: 'bg-purple-100',
    badgeClass: 'bg-purple-600 text-white',
    borderClass: 'border-purple-300',
    glowClass: 'bg-purple-500',
    cardClass: '',
    pointsClass: 'text-purple-600'
  },
  {
    name: 'LEGENDARY',
    pointsFind: 3,
    pointsHide: 3,
    stars: 3,
    starColor: 'red',
    description: 'Ultra-rare, leaderboard domination. Only the best hiders succeed!',
    iconColor: '#dc2626',
    iconBgClass: 'bg-red-100',
    badgeClass: 'bg-gradient-to-r from-red-500 to-orange-500 text-white',
    borderClass: 'border-red-300',
    glowClass: 'bg-red-500',
    cardClass: '',
    pointsClass: 'bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent'
  }
]);
</script>

<style scoped>
/* Carousel dots positioning */
.qr-carousel .v-carousel__controls {
  bottom: 8px !important;
}

/* Slightly lower the Vuetify carousel dots for mobile to free up space for arrows */
.lower-dots .v-carousel__controls {
  bottom: 18px !important;
}

.animate-float {
  animation: float 3s ease-in-out infinite;
  will-change: transform;
}

.animate-float-delay {
  animation: float 6s ease-in-out infinite;
  animation-delay: 2s;
  will-change: transform;
}

.animate-float-reverse {
  animation: floatReverse 7s ease-in-out infinite;
  animation-delay: 1s;
  will-change: transform;
}

.animate-float-subtle {
  animation: float-subtle 4s ease-in-out infinite;
  will-change: transform;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-15px) rotate(5deg);
  }
}

@keyframes floatReverse {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-10px) rotate(-3deg);
  }
}

@keyframes float-subtle {
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-8px) scale(1.05);
  }
}
</style>
